/**
 * VENDORED from tc-console/src/mdx/MermaidBlock.tsx — DO NOT EDIT.
 *
 * Refresh with scripts/sync-home.sh; edit the canonical file instead. See the
 * script header for what is copied verbatim and what is an adaptation.
 */
/**
 * MermaidBlock.tsx — fenced ```mermaid blocks render as real diagrams.
 *
 * The guide pipeline writes its mental models, ER diagrams and lifecycle
 * states as mermaid fences (the safe subset: flowchart, erDiagram,
 * stateDiagram-v2). Without this block they reach the reader as raw code;
 * with it they draw. Registered as the `mermaid` entry of MDXContent's
 * fenced-block registry — unknown to markdown, zero cost to pages without
 * one: the mermaid library is a DYNAMIC import, split into its own chunk and
 * fetched only when a page actually carries a diagram.
 *
 * THEME. The diagram re-renders on palette mode with mermaid's own
 * default/dark themes (shapes, edges) and the host theme's font; label text
 * is pinned to the host's text color so labels are legible in both modes.
 *
 * SAFETY — why the labels survive. Mermaid draws labels as HTML inside
 * <foreignObject>; a sanitizer strict enough to trust (svg-only profile)
 * strips exactly that machinery — the "shapes but no text" failure — and
 * DOMPurify v3 offers no switch to let HTML live inside SVG (its
 * HTML_INTEGRATION_POINTS is not configurable). So the rendered SVG is
 * FLATTENED first: every foreignObject label becomes a native <text> (lines
 * preserved as tspans), leaving an SVG with no HTML in it at all. The strict
 * svg-only DOMPurify pass then loses nothing. Input stays plain fenced text
 * (mermaid runs at securityLevel 'strict' — labels as text, no clicks, no
 * foreign content), and a syntax error degrades to a warning plus the
 * source — one bad diagram never breaks a page.
 */

import { useEffect, useId, useState } from 'react'
import { Alert, Box, CircularProgress, useTheme } from '@mui/material'
import DOMPurify from 'dompurify'

/** Every mermaid label rides a <foreignObject> of XHTML; flatten each to a
 *  native SVG <text> (one tspan per line, vertically centered on the box the
 *  foreignObject occupied) so the diagram survives a strict svg-only
 *  sanitize. The label's own layout (centered in its node/edge slot) is
 *  preserved because the <text> inherits the foreignObject's width/height
 *  box and its parent transform.
 *
 *  The parse is text/html, NOT image/svg+xml: mermaid's browser output is
 *  HTML-serialized (an outerHTML), so a label written `<br/>` comes back as
 *  a bare `<br>` and HTML entities like `&nbsp;` are legal — all of which a
 *  strict XML parse rejects with a parsererror document that would
 *  otherwise be serialized straight into the page (the "Unexpected closing
 *  tag" failure seen on the live guide). The HTML parser normalizes all of
 *  it; the XMLSerializer then re-emits well-formed markup. */
function flattenForeignObjects(svgString: string): string {
  const doc = new DOMParser().parseFromString(svgString, 'text/html')
  const svgRoot = doc.querySelector('svg')
  if (!svgRoot) return svgString
  const SVG_NS = 'http://www.w3.org/2000/svg'
  for (const fo of Array.from(svgRoot.getElementsByTagNameNS(SVG_NS, 'foreignObject'))) {
    const lines: string[] = []
    let cur: string[] = []
    const flush = () => {
      const s = cur.join('').trim()
      if (s) lines.push(s)
      cur = []
    }
    const walk = (node: Element) => {
      for (const child of Array.from(node.childNodes)) {
        if (child.nodeType === 3) {
          cur.push(child.textContent ?? '')
        } else if (child.nodeType === 1) {
          const tag = (child as Element).tagName.toLowerCase()
          if (tag === 'br') flush()
          else {
            walk(child as Element)
            if (tag === 'p' || tag === 'div') flush()
          }
        }
      }
    }
    walk(fo)
    flush()
    const w = parseFloat(fo.getAttribute('width') ?? '100')
    const h = parseFloat(fo.getAttribute('height') ?? '24')
    const text = doc.createElementNS(SVG_NS, 'text')
    text.setAttribute('x', String(w / 2))
    text.setAttribute('y', String(h / 2))
    text.setAttribute('text-anchor', 'middle')
    text.setAttribute('dominant-baseline', 'central')
    const useLines = lines.length ? lines : ['']
    useLines.forEach((line, i) => {
      const tspan = doc.createElementNS(SVG_NS, 'tspan')
      tspan.setAttribute('x', String(w / 2))
      tspan.setAttribute('dy', i === 0 ? `${-((useLines.length - 1) / 2) * 1.1}em` : '1.1em')
      tspan.textContent = line
      text.appendChild(tspan)
    })
    fo.replaceWith(text)
  }
  return new XMLSerializer().serializeToString(svgRoot)
}

export default function MermaidBlock({ code }: { code: string }) {
  const theme = useTheme()
  const [svg, setSvg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const renderId = `mmd${useId().replace(/[^a-zA-Z0-9]/g, '')}`

  useEffect(() => {
    let cancelled = false
    setSvg(null)
    setError(null)
    void (async () => {
      try {
        const { default: mermaid } = await import('mermaid')
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: theme.palette.mode === 'dark' ? 'dark' : 'default',
          fontFamily: theme.typography.fontFamily,
        })
        const out = await mermaid.render(renderId, code)
        const flat = flattenForeignObjects(out.svg)
        if (!cancelled) setSvg(DOMPurify.sanitize(flat, { USE_PROFILES: { svg: true, svgFilters: true } }))
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [code, renderId, theme])

  if (error) {
    return (
      <Alert severity="warning" variant="outlined" sx={{ my: 2 }}>
        This diagram could not be rendered ({error}). Its source:
        <Box component="pre" sx={{ mt: 1, mb: 0, whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>{code}</Box>
      </Alert>
    )
  }
  if (!svg) {
    return (
      <Box role="status" aria-label="rendering diagram" sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
        <CircularProgress size={22} />
      </Box>
    )
  }
  return (
    <Box
      role="img"
      aria-label="diagram"
      sx={(t) => ({
        my: 2.5,
        textAlign: 'center',
        '& svg': { maxWidth: '100%', height: 'auto' },
        // Labels are flattened to native <text> — pin them to the host text
        // color so they read in both modes (mermaid's own theme styles the
        // shapes; the words are the document's).
        '& svg text': { fill: t.palette.text.primary },
      })}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  )
}
